import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/joy';
import { useGetFrecuenciaFilterDataQuery } from '../../api/filter/dataFilterApi';
import { AvailableEmpresaFilters } from '../../utils/enums';
import { useChangeAssignedProspectadorMutation } from '../../features/empresas/empresaApi';
import { getErrorMessage } from '../../utils/errorHelpers';
import { ErrorMessage } from '../UtilitiesComponents/ErrorMessage';
import { selectGeneralInfo } from '../../store/empresaSelectedSlice';
import { FilterData } from '../../types/filter';
import { ActionButtons } from '../ui/ActionButtons';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';

interface Props {
    setShowModal: Dispatch<SetStateAction<boolean>>;
    onCancel?: () => void;
}

export function ChangeAssignedProspectador({ setShowModal, onCancel }: Props) {
    const empresaDetails = useSelector(selectGeneralInfo);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { data, isLoading: isLoadingData } = useGetFrecuenciaFilterDataQuery([AvailableEmpresaFilters.Prospectadores]);

    const prospectadores: FilterData[] = useMemo(() => {
        if(!data) return [];
        return data?.prospectadores.filter((user) => user.id !== empresaDetails?.prospectadorAsignadoId)
    }, [data]);

    const [changeProspectador, { isLoading }] = useChangeAssignedProspectadorMutation();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<{ prospectadorId: number }>();

    const onHandleSubmit = handleSubmit(async (data) => {
        try {
            const empresaId = empresaDetails?.id;

            if(!empresaId)
            {
                setErrorMessage("No se encontro el id de la empresa");
                return;
            }

            await changeProspectador({ empresaId, prospectadorId: data.prospectadorId }).unwrap()
            setShowModal(false);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    });

    if(isLoadingData)
        return (
        <div className="flex justify-center items-center p-3 h-[305px]">
            <CircularProgress />
        </div>
    );

    return (
        <div className="p-3 min-h-[305px]">
            <p className="text-xl font-semibold mb-3">Cambiar prospectador asignado</p>

            <form onSubmit={onHandleSubmit} className="flex flex-col gap-y-2">
                <div className="border border-gray-600 bg-gray-100 p-2 rounded-md">
                    <p className="text-sm font-semibold">Actual prospectador asignado</p>
                    <input 
                        value={empresaDetails?.nombreProspectador}
                        disabled
                        className="
                            w-full h-[26px] rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm bg-white disabled:bg-white"
                        type="text" />
                </div>

                <div className="flex justify-center items-center">
                    <ArrowDownwardRoundedIcon sx={{ height: 25, width: 25 }}/>
                </div>

                <div className="border border-gray-600 bg-gray-100 p-2 rounded-md mb-1">
                    <label className="text-sm font-semibold">Nuevo prospectador asignado</label>
                    <select
                        {...register("prospectadorId", {
                            required: "Debe seleccionar un nuevo prospectador",
                            min: 1,
                        })}
                        className="
                            w-full h-[26px] rounded-[3px] border-gray-400 border my-1 px-2 
                            font-semibold text-gray-600 text-sm">
                        <option value={0} hidden>Seleccionar</option>
                        {prospectadores.map((user, index) => (
                        <option value={user.id} key={index}>{user.nombre}</option>
                        ))}
                    </select>
                </div>

                {errors.prospectadorId?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.prospectadorId.message}</p>
                )}
                {errors.prospectadorId?.type === "min" && (
                    <p className="valErrorText text-sm">El tipo del vendedor es requerido</p>
                )}

                {errorMessage && (
                    <ErrorMessage>
                        {errorMessage}
                    </ErrorMessage>
                )}

                <ActionButtons 
                    isLoading={isLoading}
                    saveButtonText="Confirmar cambio"
                    onCancel={onCancel}/>
            </form>
        </div>
    );
}